/**
 * Prompts Hook
 * პრომპტების ჰუკი
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPrompts,
  getPromptSummaries,
  getPrompt,
  createPrompt,
  updatePrompt,
  updateRagConfig,
  activatePrompt,
  deactivatePrompt,
  addFileToPrompt,
  removeFileFromPrompt,
  deletePrompt,
  type CreatePromptRequest,
  type UpdatePromptRequest,
  type UpdateRagConfigRequest,
} from '../features/api/prompts';

export function usePrompts() {
  const query = useQuery({
    queryKey: ['prompts'],
    queryFn: () => getPrompts(),
  });

  return {
    prompts: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function usePromptSummaries() {
  const query = useQuery({
    queryKey: ['prompts', 'summaries'],
    queryFn: () => getPromptSummaries(),
  });

  return {
    prompts: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function usePrompt(id: string) {
  const query = useQuery({
    queryKey: ['prompt', id],
    queryFn: () => getPrompt(id),
    enabled: !!id,
  });

  return {
    prompt: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useCreatePrompt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePromptRequest) => createPrompt(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
    },
  });
}

export function useUpdatePrompt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePromptRequest }) =>
      updatePrompt(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      queryClient.invalidateQueries({ queryKey: ['prompt', id] });
    },
  });
}

export function useUpdateRagConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, config }: { id: string; config: UpdateRagConfigRequest }) =>
      updateRagConfig(id, config),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      queryClient.invalidateQueries({ queryKey: ['prompt', id] });
    },
  });
}

export function useActivatePrompt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => activatePrompt(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      queryClient.invalidateQueries({ queryKey: ['prompt', id] });
    },
  });
}

export function useDeactivatePrompt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deactivatePrompt(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      queryClient.invalidateQueries({ queryKey: ['prompt', id] });
    },
  });
}

export function useAddFileToPrompt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ promptId, fileId }: { promptId: string; fileId: string }) =>
      addFileToPrompt(promptId, fileId),
    onSuccess: (_, { promptId }) => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      queryClient.invalidateQueries({ queryKey: ['prompt', promptId] });
    },
  });
}

export function useRemoveFileFromPrompt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ promptId, fileId }: { promptId: string; fileId: string }) =>
      removeFileFromPrompt(promptId, fileId),
    onSuccess: (_, { promptId }) => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      queryClient.invalidateQueries({ queryKey: ['prompt', promptId] });
    },
  });
}

export function useDeletePrompt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deletePrompt(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
    },
  });
}
